/**
 * EmailOtpSignIn — passwordless sign-in via 6-digit email OTP.
 *
 * Wraps Better Auth's emailOTP plugin: type email → receive code →
 * type code → signed in. If no Papan account exists at that email,
 * one is auto-created (Decision 4 of the share-session unification
 * doc — auto-signup ships in V1).
 *
 * UX:
 *   1. Inline two-step form. Step 1 is the email field; clicking
 *      "Send me a code" fires sendVerificationOTP and transitions to
 *      step 2 inline (no page navigation).
 *   2. Step 2 shows the 6-digit input + verify button. On success,
 *      Better Auth sets the session cookie and we navigate to the
 *      drive root, matching the password-flow path.
 *
 * Dev-mode affordance:
 *   - When NODE_ENV !== 'production', after step 1 the component
 *     fetches /api/auth/dev/peek-otp?email=… and shows the code in
 *     a yellow callout. Mirrors multi-email.ts's devCode pattern —
 *     no need to scroll _test_emails or the API log to read the code.
 */
import { useState, useEffect, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getApiBase } from '@papan/api-client'
import { authClient } from '@papan/auth/client'
import { useAuth } from '../hooks/useAuth'
import { safeNext } from '../lib/safe-next'

const IS_DEV = import.meta.env.DEV

type Stage = 'idle' | 'sending' | 'code-entry' | 'verifying'

export function EmailOtpSignIn({ initialEmail = '' }: { initialEmail?: string }) {
  const { refreshSession } = useAuth()
  // Honor the `?next=` query param so recipients who came in via a
  // share-link verify detour land back on the share rather than the
  // generic drive root. Mirrors LoginPage's password sign-in path.
  const [searchParams] = useSearchParams()
  const nextPath = safeNext(searchParams.get('next'))

  const [stage, setStage] = useState<Stage>('idle')
  const [email, setEmail] = useState(initialEmail)
  const [code, setCode] = useState('')

  // Keep the OTP email in sync with the password-form email above so
  // the user doesn't have to re-type.
  useEffect(() => {
    if (initialEmail && initialEmail !== email) setEmail(initialEmail)
    // Only when the parent's initialEmail changes — local edits within
    // the component shouldn't get clobbered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail])
  const [error, setError] = useState('')
  const [devCode, setDevCode] = useState<string | null>(null)

  const handleSendCode = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setDevCode(null)
    if (!email.includes('@')) {
      setError('Enter a valid email address.')
      return
    }
    setStage('sending')
    await sendOtp(email)
  }

  const sendOtp = async (target: string) => {
    try {
      // Better Auth's emailOTP plugin exposes the send endpoint at
      // /api/auth/email-otp/send-verification-otp. The auto-generated
      // client method shape varies across versions, so call the
      // endpoint directly via authClient.$fetch (or fetch + cookies)
      // for stability.
      const res = await fetch(`${getApiBase()}/api/auth/email-otp/send-verification-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: target, type: 'sign-in' }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { message?: string; error?: string }
        setError(body.message || body.error || `Failed to send code (${res.status})`)
        setStage('idle')
        return
      }
      setStage('code-entry')
      if (IS_DEV) {
        try {
          const peek = await fetch(`${getApiBase()}/api/auth/dev/peek-otp?email=${encodeURIComponent(target)}`)
          if (peek.ok) {
            const body = await peek.json() as { code: string | null }
            if (body.code) setDevCode(body.code)
          }
        } catch {
          /* silent */
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code')
      setStage('idle')
    }
  }

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (code.trim().length !== 6) {
      setError('Enter the 6-digit code from your email.')
      return
    }
    setStage('verifying')
    try {
      const result = await authClient.signIn.emailOtp({ email, otp: code.trim() }) as unknown as { error?: { message?: string } | null }
      if (result.error) {
        setError(result.error.message || 'Invalid or expired code')
        setStage('code-entry')
        return
      }
      await refreshSession()
      // Honor `?next=` (gated by safeNext) so recipients return to the
      // share-view that bounced them here.
      window.location.href = nextPath
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verify failed')
      setStage('code-entry')
    }
  }

  const handleResend = async () => {
    setError('')
    setDevCode(null)
    setStage('sending')
    await sendOtp(email)
  }

  return (
    <div className="mt-3">
      {stage === 'idle' || stage === 'sending' ? (
        <form onSubmit={handleSendCode} className="space-y-2">
          {error && (
            <div className="p-2 text-xs text-red-400 bg-red-900/20 border border-red-800 rounded">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={stage === 'sending' || !email}
            className="w-full py-2.5 themed-input border rounded-lg font-medium text-sm themed-bg-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {stage === 'sending' ? 'Sending code…' : 'Sign in with email (no password)'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerify} className="space-y-2">
          {error && (
            <div className="p-2 text-xs text-red-400 bg-red-900/20 border border-red-800 rounded">
              {error}
            </div>
          )}
          <p className="text-xs themed-text-muted">
            We sent a 6-digit code to <strong className="themed-text">{email}</strong>.
            {' '}
            <button type="button" onClick={() => { setStage('idle'); setCode(''); setDevCode(null) }} className="text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline">
              Wrong email?
            </button>
          </p>
          {devCode && (
            <div className="p-2 text-xs bg-yellow-900/20 border border-yellow-800 rounded text-yellow-200">
              <strong>Dev mode:</strong> real email isn't sent locally. Your code is{' '}
              <code className="px-1.5 py-0.5 bg-yellow-900/40 rounded font-mono">{devCode}</code>.
            </div>
          )}
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={6}
            pattern="[A-Za-z0-9]{6}"
            autoComplete="one-time-code"
            autoFocus
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500 font-mono tracking-[0.4em] text-center text-base"
            placeholder="______"
          />
          <button
            type="submit"
            disabled={stage === 'verifying' || code.length !== 6}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {stage === 'verifying' ? 'Verifying…' : 'Verify and continue'}
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={stage === 'verifying'}
            className="w-full text-xs themed-text-muted hover:themed-text text-center"
          >
            Resend code
          </button>
        </form>
      )}
    </div>
  )
}
